import { DateTime, Settings } from 'luxon';
import * as React from 'react';
import { Card } from '../Card';
import { CalendarDays, CalendarDaysSelectionMode } from './CalendarDays';
import { CalendarControls } from './CalendarControls';
import { useState } from 'react';
import { Grid } from '../Grid';
import { CalendarWrapper } from './Calendar.styles';
import styled from 'styled-components';
import { IHasTestData } from '../../test-utilities/IHasTestData';
import { mergeTests } from '../../test-utilities/mergeTests';
import type { FCWithChildren } from '../../../types/FCWithChildren';

//this sets a default and so should happen far upstream instead of in a single component's declaration
Settings.defaultLocale = 'en';

const StyledGrid = styled(Grid)`
  //setting to roomy so it matches the v2 padding in position: absolute scenarios
  gap: ${(props) => props.theme.spacing.spacing5};
`;

const createInitialState = (value: string[] | string | null | undefined) => {
  const { month, year } =
    Array.isArray(value) && value.length > 0
      ? DateTime.max(...value.map((v) => DateTime.fromISO(v)))
      : typeof value === 'string' && !!value
        ? DateTime.fromISO(value)
        : DateTime.local();
  return {
    month,
    year,
  };
};

const CalendarDiv: FCWithChildren = ({ children }) => <div>{children}</div>;

export interface ICalendarValue {
  value: string | null;
  event: React.MouseEvent;
  error: string | null;
}

export interface ICalendarProps extends IHasTestData {
  /** Wraps the Calendar using the Card component. */
  inCard?: boolean;
  /** Maximum date that the user can select. Date must be in ISO8601 Date format: YYYY-MM-DD.  */
  maxDay?: string;
  /** Minimum date that the user can select. Date must be in ISO8601 Date format: YYYY-MM-DD.  */
  minDay?: string;
  /** The date value attribute of underlying input. Date must be in ISO8601 Date format: YYYY-MM-DD.  */
  inputDate: string[] | string | null | undefined;
  /** Render month as a label or control */
  monthControlType?: 'label' | 'control';
  /** A callback fired when the value prop changes. */
  onChange: (value: ICalendarValue) => void;
  /** Render year as a label or control */
  yearControlType?: 'label' | 'control';
  /** Choose 'range' to display two dates as a selected date range. Choose 'list' to display as individually selected days */
  selectionMode?: CalendarDaysSelectionMode;
  /** Optional. Shows an extra month before or after the latest selected date or the current date */
  showExtraMonths?: 'both' | 'next' | 'previous';
  /** Highlight given date in the calendar picker. */
  highlightDate?: 'today' | string;
  children?: React.ReactNode;
}

/**
 * The calendar is rendered with date based on local browser time of the client browser.
 * All dates are in the local user's timezones.
 * If you need input with popup calendar, use {@link DateInput}
 */
export const Calendar: React.FC<ICalendarProps> = (props) => {
  const [state, setState] = useState<{ year: number; month: number }>(() => createInitialState(props.inputDate));
  const CardOrDivWrapper = props.inCard === true || props.inCard === undefined ? Card : CalendarDiv;
  const normalizedMinDay = !!props.minDay ? DateTime.fromISO(props.minDay) : undefined;
  const normalizedMaxDay = !!props.maxDay ? DateTime.fromISO(props.maxDay) : undefined;
  const normalizedHighlightedDate = !!props.highlightDate
    ? props.highlightDate === 'today'
      ? DateTime.local()
      : DateTime.fromISO(props.highlightDate)
    : undefined;
  const selectedDates = Array.isArray(props.inputDate)
    ? props.inputDate.map((v) => DateTime.fromISO(v))
    : !!props.inputDate
      ? [DateTime.fromISO(props.inputDate)]
      : [];
  return (
    <CardOrDivWrapper>
      <CalendarWrapper>
        <CalendarControls
          displayDate={{ month: state.month, year: state.year }}
          monthControlType={props.monthControlType || 'label'}
          onCalendarChange={setState}
          showExtraMonths={props.showExtraMonths}
          yearControlType={props.yearControlType || 'label'}
          data-test-class={props['data-test-class']}
          data-test-id={props['data-test-id']}
        />
        <StyledGrid
          columnCount={
            props.showExtraMonths === 'both'
              ? 3
              : props.showExtraMonths === 'previous' || props.showExtraMonths === 'next'
                ? 2
                : 1
          }
        >
          {(props.showExtraMonths === 'both' || props.showExtraMonths === 'previous') && (
            <CalendarDays
              displayDate={DateTime.local(state.year, state.month).minus({ months: 1 })}
              onDayClick={props.onChange}
              minDate={normalizedMinDay}
              maxDate={normalizedMaxDay}
              selectedDates={selectedDates}
              selectionMode={props.selectionMode || 'list'}
              showNextMonthDays={false}
              highlightedDate={normalizedHighlightedDate}
              data-test-class={mergeTests(props['data-test-class'])}
              data-test-id={props['data-test-id']}
            />
          )}
          <CalendarDays
            displayDate={{ month: state.month, year: state.year }}
            onDayClick={props.onChange}
            minDate={normalizedMinDay}
            maxDate={normalizedMaxDay}
            selectedDates={selectedDates}
            selectionMode={props.selectionMode || 'list'}
            showNextMonthDays={props.showExtraMonths !== 'both' && props.showExtraMonths !== 'next'}
            showPreviousMonthDays={props.showExtraMonths !== 'both' && props.showExtraMonths !== 'previous'}
            highlightedDate={normalizedHighlightedDate}
            data-test-class={props['data-test-class']}
            data-test-id={props['data-test-id']}
          />
          {(props.showExtraMonths === 'both' || props.showExtraMonths === 'next') && (
            <CalendarDays
              displayDate={DateTime.local(state.year, state.month).plus({ months: 1 })}
              onDayClick={props.onChange}
              minDate={normalizedMinDay}
              maxDate={normalizedMaxDay}
              selectedDates={selectedDates}
              selectionMode={props.selectionMode || 'list'}
              showPreviousMonthDays={false}
              highlightedDate={normalizedHighlightedDate}
              data-test-class={mergeTests(props['data-test-class'])}
              data-test-id={props['data-test-id']}
            />
          )}
        </StyledGrid>
        {props.children}
      </CalendarWrapper>
    </CardOrDivWrapper>
  );
};
